import React, { useState } from "react";
import { AiFillWarning } from "react-icons/ai";
import { FaRegFileAlt, FaRegEdit, FaCircleNotch } from "react-icons/fa";
import { IoIosArrowDropright } from "react-icons/io";
import { Observer } from "mobx-react";
import PdfViewer from "../reusble_components/pdfViewer";
import ImageViewer from "../reusble_components/ImageViewer";
import { useStores } from "../../store";

const TaskCard = ({ assessment, applicationId, startedAt, submittedAt }) => {
  const { RecuiterStore } = useStores();
  const [showDetails, setShowDetails] = useState(false);
  const [showPdf, setShowPdf] = useState(false);
  const [pdfUrl, setPdfUrl] = useState("");
  const [showImage, setShowImage] = useState(false);
  const [imageUrl, setImageUrl] = useState("");
  const [showEditDurationModal, setShowEditDurationModal] = useState(false);
  const [editingDuration, setEditingDuration] = useState("");
  const [isUpdatingDuration, setIsUpdatingDuration] = useState(false);

  if (!assessment) return null;

  const getColor = (status) => {
    if (status === 0) return "bg-orange-400";
    if (status === 1) return "bg-blue-500";
    if (status === 2) return "bg-primary";
    if (status === 3) return "bg-green-500";
    return "bg-orange-400";
  };

  const getBorderColor = (status) => {
    if (status === 0) return "border-orange-400";
    if (status === 1) return "border-blue-500";
    if (status === 2) return "border-primary";
    if (status === 3) return "border-green-500";
    return "border-orange-400";
  };

  const getTextColor = (status) => {
    if (status === 0) return "text-orange-400";
    if (status === 1) return "text-blue-500";
    if (status === 2) return "text-primary";
    if (status === 3) return "text-green-500";
    return "text-orange-400";
  };

  const calculateLateness = () => {
    // Prioritize props passed from parent, fallback to internal fields if needed
    const startObj = startedAt || assessment?.createdAt;
    const submittedObj =
      submittedAt || (assessment?.updatedAt ?? assessment?.result?.updatedAt);
    const durationDays =
      assessment?.taskDuration ?? assessment?.assessmentData?.duration;

    if (!startObj || !submittedObj || !durationDays) return null;

    const start = new Date(startObj);
    const submitted = new Date(submittedObj);
    const deadline = new Date(start);
    deadline.setDate(deadline.getDate() + parseInt(durationDays));

    if (submitted > deadline) {
      const diffMs = submitted - deadline;
      const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
      const diffHrs = Math.floor(
        (diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60),
      );
      const diffMins = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));

      let lateness = "";
      if (diffDays > 0) lateness += `${diffDays}d `;
      if (diffHrs > 0) lateness += `${diffHrs}h `;
      if (diffMins > 0) lateness += `${diffMins}m`;

      return lateness.trim();
    }
    return null;
  };

  const lateness = calculateLateness();

  return (
    <Observer>
      {() => (
        <div className="bg-white rounded-lg pb-6 ">
          <div className="flex justify-between items-center mb-4 pr-2">
            <h2 className="text-xl flex items-center gap-x-2 font-semibold">
              Task{" "}
              {!showDetails && (
                <h2 className="text-base font-normal gil-med inline-flex items-center">
                  (
                  <span className="inline-block max-w-[200px] sm:max-w-[300px] md:max-w-[24rem] truncate">
                    {assessment?.task?.title ??
                      assessment?.assessmentData?.title}
                  </span>
                  )
                </h2>
              )}
            </h2>
            <button
              className="text-primary hover:underline flex text-sm items-center gap-x-1 self-start sm:self-center"
              onClick={() => setShowDetails(!showDetails)}
            >
              {showDetails ? "Hide Details" : "View Details"}
              <span
                className={`text-sm transition-transform ${
                  showDetails ? "rotate-90" : ""
                }`}
              >
                <IoIosArrowDropright />
              </span>
            </button>
          </div>

          {showDetails ? (
            <div className="space-y-4 pb-4">
              <div className="flex flex-col sm:flex-row gap-2">
                <h3 className="text-gray-600 min-w-[60px]">Title:</h3>
                <p className="font-medium">
                  {assessment?.task?.title ?? assessment?.assessmentData?.title}
                </p>
              </div>

              <div>
                <h3 className="text-gray-600 mb-1">Description:</h3>
                {assessment?.task?.description ? (
                  <p className="text-gray-800 whitespace-pre-wrap">
                    {assessment?.task?.description}
                  </p>
                ) : (
                  (() => {
                    const decodeHtml = (html) => {
                      if (typeof window === "undefined") return html || "";
                      const textarea = document.createElement("textarea");
                      textarea.innerHTML = html || "";
                      return textarea.value;
                    };

                    const decodedDescription = decodeHtml(
                      assessment?.assessmentData?.description || "",
                    );
                    const containsHtml = /<[^>]*>/g.test(decodedDescription);

                    return containsHtml ? (
                      <div
                        className="text-gray-800 [&>h1]:text-2xl [&>h1]:font-semibold [&>h1]:mb-3 [&>h2]:text-xl [&>h2]:font-semibold [&>h2]:mb-2 [&>h3]:text-lg [&>h3]:font-semibold [&>h3]:mb-2 [&>p]:mb-3 [&>p]:leading-relaxed [&>ul]:list-disc [&>ul]:ml-6 [&>ul]:mb-3 [&>ol]:list-decimal [&>ol]:ml-6 [&>ol]:mb-3 [&>li]:mb-1 [&>a]:text-primary [&>a]:underline [&>a]:hover:text-primary-dark"
                        dangerouslySetInnerHTML={{ __html: decodedDescription }}
                      />
                    ) : (
                      <p className="text-gray-800 whitespace-pre-wrap">
                        {decodedDescription}
                      </p>
                    );
                  })()
                )}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                {!assessment?.task && (
                  <div className="flex gap-x-2">
                    <h3 className="text-gray-600 min-w-[60px]">Level:</h3>
                    <p className="capitalize">
                      {assessment.assessmentData.levelOfAssessment}
                    </p>
                  </div>
                )}
                <div className="flex gap-x-2">
                  <h3 className="text-gray-600 min-w-[60px]">Type:</h3>
                  <p className="capitalize">
                    {assessment.assessmentData.assessmentType}
                  </p>
                </div>
                <div className="flex gap-x-2 items-center">
                  <h3 className="text-gray-600 min-w-[60px]">Duration:</h3>
                  <div className="flex items-center gap-2">
                    <p>
                      {assessment?.taskDuration ??
                        assessment?.assessmentData?.duration}{" "}
                      days
                    </p>
                    {applicationId && (
                      <button
                        onClick={() => {
                          setEditingDuration(
                            assessment?.taskDuration ??
                              assessment?.assessmentData?.duration ??
                              "",
                          );
                          setShowEditDurationModal(true);
                        }}
                        className="p-1 rounded-full hover:bg-primary/10 transition"
                        title="Edit Duration"
                      >
                        <FaRegEdit className="text-primary text-sm" />
                      </button>
                    )}
                  </div>
                </div>
              </div>

              {assessment.assessmentData?.technologyUsed &&
                assessment.assessmentData?.technologyUsed.length > 0 && (
                  <div className="flex flex-col gap-2 ">
                    <h4 className="text-gray-600">Technologies:</h4>
                    <div className="flex flex-wrap gap-2">
                      {assessment.assessmentData.technologyUsed.map(
                        (item, index) => (
                          <p
                            className="text-sm bg-gray-100 p-1 px-4 rounded-full"
                            key={index}
                          >
                            {item}
                          </p>
                        ),
                      )}
                    </div>
                  </div>
                )}

              {assessment.assessmentData.githubLink && (
                <div className="flex flex-col gap-2">
                  <h4 className="text-gray-600">Github:</h4>
                  <div className="flex flex-wrap gap-2">
                    <p className="text-sm rounded-full">
                      {assessment.assessmentData.githubLink}
                    </p>
                  </div>
                </div>
              )}

              {assessment.assessmentData?.media &&
                assessment.assessmentData?.media.length > 0 && (
                  <div>
                    <h4 className="text-gray-600 mb-2">Media Resources:</h4>
                    <div className="flex flex-col gap-2">
                      {assessment.assessmentData?.media.map((item, index) => (
                        <a
                          key={item._id}
                          href={item.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-2 text-primary hover:underline break-all"
                        >
                          <FaRegFileAlt className="flex-shrink-0" />
                          <span>Media {index + 1}</span>
                        </a>
                      ))}
                    </div>
                  </div>
                )}
            </div>
          ) : null}

          {assessment?.isCompleted ? (
            <div className="mt-6 px-0">
              <div className="flex flex-row justify-between items-center gap-y-2 mb-4">
                <div>
                  <h3 className="text-lg font-semibold">
                    Task Submission Details
                  </h3>
                  {lateness && (
                    <span className="text-red-500 font-medium text-sm">
                      Late Submission ( {lateness} )
                    </span>
                  )}
                </div>
                <span
                  className={`${
                    assessment.isCompleted
                      ? "bg-[#20BF55]"
                      : `${getColor(assessment.result.status)}`
                  } text-white px-3 py-1 rounded-md text-sm self-start sm:self-auto`}
                >
                  {assessment.isCompleted
                    ? "Task Completed"
                    : assessment.result.status === 1
                      ? "Task is started"
                      : assessment.result.status === 2
                        ? "Task is in progress"
                        : assessment.result.status === 3
                          ? "Task is completed"
                          : "Task is pending"}
                </span>
              </div>
              <div className="space-y-4">
                <div>
                  <p className="text-gray-700">
                    {assessment.result?.candidateFeedback}
                  </p>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {assessment.result?.githubLink && (
                    <div>
                      <h4 className="text-gray-600 mb-1 gil-med">
                        GitHub Link
                      </h4>
                      {assessment.result?.githubLink ? (
                        <a
                          href={assessment.result?.githubLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-primary hover:underline break-all"
                        >
                          {assessment.result?.githubLink}
                        </a>
                      ) : (
                        <p className="text-gray-500">Not provided</p>
                      )}
                    </div>
                  )}
                  {assessment.result?.hostUrl && (
                    <div>
                      <h4 className="text-gray-600 mb-1 gil-med">Host URL</h4>
                      {assessment.result?.hostUrl ? (
                        <a
                          href={assessment.result?.hostUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-primary hover:underline break-all"
                        >
                          {assessment.result?.hostUrl}
                        </a>
                      ) : (
                        <p className="text-gray-500">Not provided</p>
                      )}
                    </div>
                  )}
                </div>

                {assessment.result?.media &&
                  assessment.result?.media.length > 0 && (
                    <div>
                      <h4 className="text-gray-600 mb-2 gil-med">
                        Attachments
                      </h4>
                      <div className="flex flex-wrap gap-4">
                        {assessment.result?.media.map((item, index) => {
                          const url = item.url || "";
                          const isPdf = url.toLowerCase().endsWith(".pdf");
                          const isImage = /\.(jpg|jpeg|png|gif|webp)$/i.test(
                            url,
                          );
                          const fileName =
                            url.split("/").pop() || `Attachment ${index + 1}`;
                          return (
                            <div
                              key={item._id}
                              className="flex flex-col items-center bg-gray-50 border border-gray-200 rounded-lg p-3 min-w-[160px] max-w-[220px] shadow-sm"
                            >
                              <div className="flex items-center gap-2 mb-2">
                                <FaRegFileAlt
                                  className={`text-2xl ${
                                    isPdf
                                      ? "text-primary"
                                      : isImage
                                        ? "text-green-500"
                                        : "text-gray-400"
                                  }`}
                                />
                                <span
                                  className="text-sm font-medium text-gray-800 truncate max-w-[120px]"
                                  title={fileName}
                                >
                                  {fileName}
                                </span>
                              </div>
                              {isPdf ? (
                                <button
                                  className="bg-primary text-white px-3 py-1 rounded hover:bg-primary/90 text-xs transition"
                                  onClick={() => {
                                    setPdfUrl(url);
                                    setShowPdf(true);
                                  }}
                                >
                                  View PDF
                                </button>
                              ) : isImage ? (
                                <button
                                  className="bg-green-500 text-white px-3 py-1 rounded hover:bg-green-600 text-xs transition"
                                  onClick={() => {
                                    setImageUrl(url);
                                    setShowImage(true);
                                  }}
                                >
                                  View Image
                                </button>
                              ) : (
                                <a
                                  href={url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="bg-gray-200 text-primary px-3 py-1 rounded hover:bg-primary hover:text-white text-xs transition"
                                  download
                                >
                                  Download File
                                </a>
                              )}
                            </div>
                          );
                        })}
                      </div>
                      {showPdf && (
                        <PdfViewer
                          pdfUrl={pdfUrl}
                          onClose={() => setShowPdf(false)}
                        />
                      )}
                      {showImage && (
                        <ImageViewer
                          imageUrl={imageUrl}
                          onClose={() => setShowImage(false)}
                        />
                      )}
                    </div>
                  )}
              </div>
            </div>
          ) : (
            <div
              className={` ${getColor(
                assessment?.result?.status,
              )} bg-opacity-10 border-l-4 ${getBorderColor(
                assessment?.result?.status,
              )} p-4 rounded-r-lg`}
            >
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-y-3">
                <div className="flex items-center gap-3">
                  <AiFillWarning
                    className={`${getTextColor(
                      assessment?.result?.status,
                    )} text-2xl flex-shrink-0`}
                  />
                  <span className="text-gray-700 font-medium">
                    {assessment?.result?.status &&
                    assessment?.result?.status === 1
                      ? "Task is started"
                      : assessment?.result?.status === 2
                        ? "Task is in progress"
                        : assessment?.result?.status === 3
                          ? "Task is completed"
                          : "Task is pending"}
                  </span>
                </div>
              </div>

              {!assessment?.isCompleted && (
                <div className="mt-4">
                  <p className="text-sm text-gray-600">
                    The candidate needs to complete this section as part of the
                    assessment process.
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Edit Duration Modal */}
          {showEditDurationModal && (
            <div
              className="fixed inset-0 z-50 flex items-center justify-center backdrop-blur-sm bg-black bg-opacity-50"
              onClick={() =>
                !isUpdatingDuration && setShowEditDurationModal(false)
              }
            >
              <div
                onClick={(e) => e.stopPropagation()}
                className="bg-white p-6 rounded-lg shadow-lg w-[90%] md:w-[400px]"
              >
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-xl font-semibold text-gray-800">
                    Edit Task Duration
                  </h3>
                  {!isUpdatingDuration && (
                    <button
                      onClick={() => setShowEditDurationModal(false)}
                      className="text-gray-400 hover:text-gray-600"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-6 w-6"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M6 18L18 6M6 6l12 12"
                        />
                      </svg>
                    </button>
                  )}
                </div>

                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Duration (days)
                  </label>
                  <input
                    type="number"
                    min="1"
                    value={editingDuration}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (
                        value === "" ||
                        (parseInt(value) >= 1 && parseInt(value) <= 365)
                      ) {
                        setEditingDuration(value);
                      }
                    }}
                    onWheel={(e) => e.target.blur()}
                    disabled={isUpdatingDuration}
                    className="w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary disabled:bg-gray-100"
                    placeholder="Enter duration in days"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Enter a value between 1 and 365 days
                  </p>
                </div>

                <div className="flex justify-end gap-3">
                  <button
                    onClick={() => {
                      if (!isUpdatingDuration) {
                        setShowEditDurationModal(false);
                        setEditingDuration("");
                      }
                    }}
                    disabled={isUpdatingDuration}
                    className="px-4 py-2 text-gray-600 hover:text-gray-800 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={async () => {
                      const durationValue = parseInt(editingDuration);
                      if (
                        !editingDuration ||
                        isNaN(durationValue) ||
                        durationValue < 1
                      ) {
                        alert("Please enter a valid duration (minimum 1 day)");
                        return;
                      }

                      setIsUpdatingDuration(true);
                      try {
                        const success =
                          await RecuiterStore.updateApplicantTaskDuration(
                            applicationId,
                            durationValue,
                          );
                        if (success) {
                          setShowEditDurationModal(false);
                          setEditingDuration("");
                          alert("Task duration updated successfully!");
                        } else {
                          alert(
                            "Failed to update task duration. Please try again.",
                          );
                        }
                      } catch (error) {
                        console.error("Error updating task duration:", error);
                        alert(
                          "An error occurred while updating task duration.",
                        );
                      } finally {
                        setIsUpdatingDuration(false);
                      }
                    }}
                    disabled={
                      isUpdatingDuration ||
                      !editingDuration ||
                      parseInt(editingDuration) < 1
                    }
                    className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  >
                    {isUpdatingDuration ? (
                      <>
                        <FaCircleNotch className="animate-spin" />
                        Updating...
                      </>
                    ) : (
                      "Save"
                    )}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </Observer>
  );
};

export default TaskCard;
